import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts"
import { DisaggregatedMetric } from "../../services/types"
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import { formatValue } from "../../utils/helpers";
import { useDashboardColor } from "../../context/DashboardColorContext";

interface BarChartOnlyProps {
  metric: DisaggregatedMetric | undefined;
  barColor?: string;
  height: string;
  background: string;
}

// Helper for custom toolbar styling and value formatting (expand NH for race disaggregations)
const CustomTooltipBar = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-2 border border-slate-200 rounded shadow-sm text-xs">
        <p>{`${label.replace(/\bNH\b/, "Non-Hispanic")}`}</p>
        <p>{`Value: ${formatValue(payload[0].value, payload[0].payload.unit)}`}</p>
      </div>
    )
  }
  return null
};

// Helper for more cleanly wrapping axis label text
const CustomTick = (props: any) => {
  const { x, y, payload } = props;
  const maxLineLength = 15; // max chars per line before wrapping
  const words = payload.value.split(' ');

  // Helper to split words into lines <= maxLineLength chars
  const lines: string[] = [];
  let currentLine = '';

  words.forEach((word: any) => {
    if ((currentLine + word).length <= maxLineLength) {
      currentLine += (currentLine ? ' ' : '') + word;
    } else {
      if (currentLine) lines.push(currentLine);
      currentLine = word;
    }
  });
  if (currentLine) lines.push(currentLine);

  // If only one line, render single text, else render tspans with dy to wrap vertically
  if (lines.length === 1) {
    return (
      <text
        x={x}
        y={y}
        dy={2}
        textAnchor="end"
        fill="#666"
        style={{ whiteSpace: 'nowrap', fontSize: 9 }}
      >
        {lines[0]}
      </text>
    );
  }

  // Multiple lines: render each line in a <tspan> with vertical offset
  return (
    <text
      x={x}
      y={y}
      textAnchor="end"
      fill="#666"
      style={{ fontSize: 9 }}
    >
      {lines.map((line, i) => (
        <tspan key={i} x={x} dy={i === 0 ? -2 : 10}>
          {line}
        </tspan>
      ))}
    </text>
  );
};

const MetricBarChart = ({ metric, height, background }: BarChartOnlyProps) => {
    const color = useDashboardColor();
    // Wait for metric and data to be available
    if (!metric || !metric.versions || metric.versions.length === 0 || !metric.versions[0].disaggregations) {
        return <div className="h-24 flex items-center justify-center text-xs text-slate-500"><Skeleton height={24} width={"100%"}/></div>;
    }

    const versions = metric?.versions || [];
    const disaggregations = versions[0].disaggregations;
    const sorted = [...disaggregations].sort((a, b) => b.value - a.value)
    const unit = versions[0]?.unit

    const chartData = sorted.map(d => ({
        name: d.name,
        value: d.value ?? 0,
        unit: unit,
    }))

    if (chartData.length === 0) {
        return <div className="h-24 flex items-center justify-center text-xs text-slate-500">No data available</div>
    }

    return (
        <div className={`${height} ${background}`}>
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
            data={chartData}
            layout="vertical"
            margin={{ top: 10, right: 30, left: 5, bottom: 0 }}
            >
            <XAxis
                type="number"
                tick={{ fontSize: 9 }}
                tickFormatter={(value) => formatValue(value, unit)}
            />
            <YAxis
                type="category"
                dataKey="name"
                tick={<CustomTick/>}
                width={100}
            />
            <Tooltip content={<CustomTooltipBar />} cursor={{fill: 'transparent'}} />
            <Bar dataKey="value" fill={color} activeBar={false} />
            </BarChart>
        </ResponsiveContainer>
        </div>
    )
}

export default MetricBarChart;